/* ------------------------------------------------------------------
   Bulk Pricing Slider — vanilla JS, no dependencies.
   - Live price calculator (regular / discount / per-unit / final / savings)
   - Quick-jump buttons + free dragging, keyboard accessible (native <input type=range>)
   - Tier detection driven entirely by the tiers array injected via Liquid (no hardcoded discounts)
   - Native Shopify AJAX Cart API for add-to-cart (POST /cart/add.js) — no custom cart logic
   - Frontend only PREVIEWS savings; real discounting happens via Shopify Automatic Discounts
------------------------------------------------------------------- */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function formatMoney(cents, symbol) {
    var amount = (Math.round(cents) / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return symbol + parts.join('.');
  }

  function fillTemplate(str, map) {
    return str.replace(/\{(\w+)\}/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : m;
    });
  }

  function findCurrentTier(tiers, qty) {
    var applicable = null;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].qty) applicable = tiers[i];
    }
    return applicable;
  }

  function findNextTier(tiers, qty) {
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].qty > qty) return tiers[i];
    }
    return null;
  }

  function animateValue(el, from, to, formatter, duration) {
    // requestAnimationFrame is paused/throttled while the tab/document is not
    // visible (e.g. a backgrounded tab). Skip straight to the final value in
    // that case so the displayed price never gets stuck mid-count.
    if (from === to || (typeof document !== 'undefined' && document.hidden)) {
      el.textContent = formatter(to);
      return;
    }
    var start = null;
    function step(ts) {
      if (start === null) start = ts;
      var progress = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - progress, 3);
      var val = from + (to - from) * eased;
      el.textContent = formatter(val);
      if (progress < 1) window.requestAnimationFrame(step);
      else el.textContent = formatter(to);
    }
    window.requestAnimationFrame(step);
  }

  function initSlider(root) {
    var configEl = root.querySelector('[data-bps-config]');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    var slider = root.querySelector('[data-bps-slider]');
    var track = root.querySelector('[data-bps-track]');
    var qtyDisplay = root.querySelector('[data-bps-qty-display]');
    var tierBadge = root.querySelector('[data-bps-tier-badge]');
    var totalPriceEl = root.querySelector('[data-bps-total-price]');
    var regularPriceEl = root.querySelector('[data-bps-regular-price]');
    var discountPctEl = root.querySelector('[data-bps-discount-pct]');
    var discountAmtEl = root.querySelector('[data-bps-discount-amt]');
    var unitPriceEl = root.querySelector('[data-bps-unit-price]');
    var finalPriceEl = root.querySelector('[data-bps-final-price]');
    var savingsEl = root.querySelector('[data-bps-savings]');
    var progressFill = root.querySelector('[data-bps-progress-fill]');
    var progressText = root.querySelector('[data-bps-progress-text]');
    var freeShipEl = root.querySelector('[data-bps-free-ship]');
    var freeShipTextEl = root.querySelector('[data-bps-free-ship-text]');
    var ctaButton = root.querySelector('[data-bps-cta]');
    var ctaNote = root.querySelector('[data-bps-cta-note]');
    var quoteButton = root.querySelector('[data-bps-quote-cta]');
    var quoteNote = root.querySelector('[data-bps-quote-note]');
    var summaryCard = root.querySelector('[data-bps-summary-card]');
    var jumpButtons = root.querySelectorAll('[data-bps-jump]');

    if (!slider || !ctaButton) return;

    var state = {
      price: config.variantPrice,
      available: config.available,
      variantId: config.variantId,
      lastTierQty: null
    };

    function render(qty, opts) {
      opts = opts || {};
      qty = Math.max(config.minQty, Math.min(config.maxQty, qty));
      slider.value = qty;
      slider.setAttribute('aria-valuenow', String(qty));

      var pctOfRange = ((qty - config.minQty) / (config.maxQty - config.minQty)) * 100;
      track.style.setProperty('--bps-progress', pctOfRange + '%');

      var tier = findCurrentTier(config.tiers, qty);
      var pct = tier ? tier.pct : 0;
      var unitPrice = state.price * (1 - pct / 100);
      var finalTotal = unitPrice * qty;
      var regularTotal = state.price * qty;
      var savings = regularTotal - finalTotal;
      var fillColor = tier ? tier.color : config.baseColor;

      track.style.setProperty('--bps-fill-color', fillColor);

      qtyDisplay.textContent = qty + (qty === 1 ? ' Unit' : ' Units');

      if (tierBadge) {
        if (tier) {
          tierBadge.hidden = false;
          tierBadge.textContent = tier.label;
          tierBadge.style.backgroundColor = tier.color;
        } else {
          tierBadge.hidden = true;
        }
      }

      var animate = opts.animate !== false;
      var duration = 250;
      if (animate) {
        animateValue(regularPriceEl, parseFloat(regularPriceEl.dataset.raw || regularTotal) , regularTotal, function (v) { return formatMoney(v, config.currencySymbol); }, duration);
        animateValue(finalPriceEl, parseFloat(finalPriceEl.dataset.raw || finalTotal), finalTotal, function (v) { return formatMoney(v, config.currencySymbol); }, duration);
        animateValue(savingsEl, parseFloat(savingsEl.dataset.raw || savings), savings, function (v) { return formatMoney(v, config.currencySymbol); }, duration);
        if (totalPriceEl) {
          animateValue(totalPriceEl, parseFloat(totalPriceEl.dataset.raw || finalTotal), finalTotal, function (v) { return formatMoney(v, config.currencySymbol); }, duration);
        }
      } else {
        regularPriceEl.textContent = formatMoney(regularTotal, config.currencySymbol);
        finalPriceEl.textContent = formatMoney(finalTotal, config.currencySymbol);
        savingsEl.textContent = formatMoney(savings, config.currencySymbol);
        if (totalPriceEl) totalPriceEl.textContent = formatMoney(finalTotal, config.currencySymbol);
      }
      regularPriceEl.dataset.raw = regularTotal;
      finalPriceEl.dataset.raw = finalTotal;
      savingsEl.dataset.raw = savings;
      if (totalPriceEl) totalPriceEl.dataset.raw = finalTotal;

      discountPctEl.textContent = pct + '%';
      discountAmtEl.textContent = formatMoney(savings, config.currencySymbol);
      unitPriceEl.textContent = formatMoney(unitPrice, config.currencySymbol);

      var next = findNextTier(config.tiers, qty);
      if (next) {
        var floorQty = tier ? tier.qty : config.minQty;
        var range = Math.max(1, next.qty - floorQty);
        var progressed = qty - floorQty;
        var progressPct = Math.max(0, Math.min(100, (progressed / range) * 100));
        progressFill.style.width = progressPct + '%';
        progressFill.style.backgroundColor = fillColor;
        progressText.hidden = false;
        progressText.textContent = fillTemplate(config.progressTemplate, {
          remaining: next.qty - qty,
          discount: next.pct
        });
      } else if (tier) {
        progressFill.style.width = '100%';
        progressFill.style.backgroundColor = tier.color;
        progressText.hidden = false;
        progressText.textContent = fillTemplate(config.unlockedTemplate, { discount: tier.pct });
      } else {
        progressFill.style.width = '0%';
        progressText.hidden = true;
      }

      // Free shipping unlock messaging — stays hidden entirely until the
      // threshold is met, then reveals only the "unlocked" state (no
      // countdown/progress text). Use typeof check (not truthy check) so a
      // threshold of 0 — products that always ship free per their Shopify
      // shipping profile — correctly shows as unlocked immediately instead
      // of being skipped as falsy.
      if (freeShipEl && freeShipTextEl && typeof config.freeShippingThreshold === 'number') {
        if (finalTotal >= config.freeShippingThreshold) {
          freeShipEl.hidden = false;
          freeShipEl.classList.add('is-unlocked');
          freeShipTextEl.textContent = config.freeShippingUnlockedTemplate;
        } else {
          freeShipEl.hidden = true;
          freeShipEl.classList.remove('is-unlocked');
        }
      }

      var newTierQty = tier ? tier.qty : 0;
      if (state.lastTierQty !== null && newTierQty > state.lastTierQty) {
        root.classList.add('bps-tier-unlocked');
        window.setTimeout(function () {
          root.classList.remove('bps-tier-unlocked');
        }, 700);
      }
      state.lastTierQty = newTierQty;

      if (state.available) {
        ctaButton.disabled = false;
        ctaButton.textContent = fillTemplate(config.ctaTemplate, { qty: qty });
      } else {
        ctaButton.disabled = true;
        ctaButton.textContent = 'Out of Stock';
      }
      ctaButton.dataset.qty = qty;

      // Above the top tier quantity, standard self-serve discounting caps out —
      // show a bulk-quote option ALONGSIDE Add to Cart (never hide Add to Cart).
      var overQuoteThreshold = config.bulkQuoteQty && qty > config.bulkQuoteQty;
      if (quoteButton) {
        quoteButton.hidden = !overQuoteThreshold;
        if (quoteNote) {
          quoteNote.hidden = !overQuoteThreshold;
          if (overQuoteThreshold) {
            var capTier = findCurrentTier(config.tiers, config.bulkQuoteQty);
            quoteNote.textContent = fillTemplate(config.bulkQuoteNoteTemplate || '', {
              discount: capTier ? capTier.pct : pct,
              threshold: config.bulkQuoteQty
            });
          }
        }
        if (summaryCard) summaryCard.classList.toggle('bps__summary-card--capped', !!overQuoteThreshold);
      }
    }

    slider.addEventListener('input', function () {
      render(parseInt(slider.value, 10));
      jumpButtons.forEach(function (btn) {
        btn.classList.toggle('is-active', parseInt(btn.dataset.bpsJump, 10) === parseInt(slider.value, 10));
      });
    });

    jumpButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var qty = parseInt(btn.dataset.bpsJump, 10);
        render(qty);
        jumpButtons.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
      });
    });

    ctaButton.addEventListener('click', function () {
      if (!state.available || ctaButton.disabled) return;
      var qty = parseInt(slider.value, 10);

      ctaButton.disabled = true;
      ctaButton.classList.add('is-loading');
      if (ctaNote) ctaNote.hidden = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: state.variantId, quantity: qty }] })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          ctaButton.classList.remove('is-loading');
          if (!result.ok) {
            throw new Error(result.data && result.data.description ? result.data.description : 'Could not add to cart.');
          }
          ctaButton.classList.add('is-success');
          if (ctaNote) {
            ctaNote.hidden = false;
            ctaNote.textContent = 'Added ' + qty + ' items to cart';
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: result.data } }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          root.dispatchEvent(new CustomEvent('bps:added', { bubbles: true, detail: { quantity: qty, variantId: state.variantId } }));
          window.setTimeout(function () {
            ctaButton.classList.remove('is-success');
          }, 1800);
        })
        .catch(function (err) {
          ctaButton.classList.remove('is-loading');
          if (ctaNote) {
            ctaNote.hidden = false;
            ctaNote.textContent = err.message || 'Something went wrong. Please try again.';
            ctaNote.style.color = '#dc2626';
          }
        })
        .finally(function () {
          ctaButton.disabled = !state.available;
        });
    });

    // React to variant changes on the product form (standard Shopify pattern:
    // the variant picker updates a hidden input/select named "id").
    var form = root.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (form) {
      var idField = form.querySelector('input[name="id"], select[name="id"]');
      if (idField) {
        var applyVariant = function (variantId) {
          var v = config.variantsById && config.variantsById[String(variantId)];
          if (!v) return;
          state.price = v.price;
          state.available = v.available;
          state.variantId = parseInt(variantId, 10);
          render(parseInt(slider.value, 10), { animate: true });
        };
        idField.addEventListener('change', function () {
          applyVariant(idField.value);
        });
        // Some theme variant pickers update the value programmatically without
        // firing a native change event — cover that case too.
        var observer = new MutationObserver(function () {
          applyVariant(idField.value);
        });
        observer.observe(idField, { attributes: true, attributeFilter: ['value'] });
        document.addEventListener('variant:change', function (evt) {
          if (evt.detail && evt.detail.variant && evt.detail.variant.id) {
            applyVariant(evt.detail.variant.id);
          }
        });
      }
    }

    render(config.initialQty, { animate: false });
  }

  ready(function () {
    var roots = document.querySelectorAll('[data-bps-root]');
    roots.forEach(initSlider);
  });
})();
